import { useState } from "react";
import type { AreaPiloto, LayerToggle, AreaVerdeEntorno } from "@/types/piloto";

interface PilotSidebarPanelProps {
  area: AreaPiloto;
  camadas: LayerToggle[];
  areasVerdes: AreaVerdeEntorno[];
  activeLayers: Set<string>;
  onToggleLayer: (id: string) => void;
}

export default function PilotSidebarPanel({ area, camadas, areasVerdes, activeLayers, onToggleLayer }: PilotSidebarPanelProps) {
  const [activeTab, setActiveTab] = useState<"identificacao" | "camadas" | "infra">("identificacao");

  return (
    <div className="w-full h-full bg-white border-r border-background-200/70 flex flex-col shrink-0 overflow-hidden">
      {/* Tabs */}
      <div className="flex border-b border-background-200/70">
        {[
          { id: "identificacao" as const, label: "Identificação", icon: "ri-information-line" },
          { id: "camadas" as const, label: "Camadas", icon: "ri-stack-line" },
          { id: "infra" as const, label: "Infraestrutura", icon: "ri-plant-line" },
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 flex items-center justify-center gap-1 py-2.5 text-[10px] font-medium transition-colors whitespace-nowrap ${
              activeTab === tab.id
                ? "text-primary-600 border-b-2 border-primary-500 bg-primary-50/50"
                : "text-foreground-500 hover:text-foreground-700 hover:bg-background-50"
            }`}
          >
            <i className={`${tab.icon} text-xs`}></i>
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-3 space-y-3">
        {activeTab === "identificacao" && (
          <>
            {/* Card 1 — Identificação */}
            <div className="bg-background-50 rounded-card border border-background-200/70 p-3">
              <h3 className="text-xs font-semibold text-foreground-800 font-heading mb-2 flex items-center gap-1.5">
                <i className="ri-map-pin-line text-primary-500 text-xs"></i>
                Identificação da Área
              </h3>
              <div className="space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-[10px] text-foreground-500">Nome</span>
                  <span className="text-[10px] font-medium text-foreground-800 text-right">{area.nome}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[10px] text-foreground-500">Município</span>
                  <span className="text-[10px] font-medium text-foreground-800">{area.municipio}/{area.uf}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[10px] text-foreground-500">Código</span>
                  <span className="text-[10px] font-mono text-foreground-700">{area.id}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[10px] text-foreground-500">Total de Quadras</span>
                  <span className="text-[10px] font-semibold text-primary-600">{area.totalQuadras}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[10px] text-foreground-500">Status</span>
                  <span className="text-[10px] font-medium text-accent-600">{area.status}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-[10px] text-foreground-500">Coordenada Central</span>
                  <span className="text-[10px] font-mono text-foreground-700">
                    {area.coordenadaCentral.latitude.toFixed(7)}, {area.coordenadaCentral.longitude.toFixed(7)}
                  </span>
                </div>
              </div>
            </div>

            {/* Card 2 — Contexto Ambiental */}
            <div className="bg-background-50 rounded-card border border-background-200/70 p-3">
              <h3 className="text-xs font-semibold text-foreground-800 font-heading mb-2 flex items-center gap-1.5">
                <i className="ri-landscape-line text-accent-500 text-xs"></i>
                Contexto Ambiental
              </h3>
              <p className="text-[10px] text-foreground-600 leading-relaxed">
                O Setor Bela Vista está localizado em uma região estratégica de Goiânia, próximo ao Jardim Botânico,
                Parque Areião, Parque Flamboyant, Parque das Laranjeiras e outras áreas verdes urbanas. Por isso,
                a área será analisada como possível conector verde entre fragmentos vegetados, permitindo avaliar
                cobertura de copa, déficit arbóreo, conflitos urbanos e prioridade de plantio.
              </p>
              <div className="mt-2 flex items-center gap-1.5">
                <span className="text-[10px] text-foreground-500">Função ambiental:</span>
                <span className="text-[10px] font-medium text-primary-600 bg-primary-50 px-1.5 py-0.5 rounded">
                  {area.funcaoAmbiental}
                </span>
              </div>
            </div>

            {/* Ações futuras */}
            <div className="bg-background-50 rounded-card border border-background-200/70 p-3">
              <h3 className="text-xs font-semibold text-foreground-800 font-heading mb-2 flex items-center gap-1.5">
                <i className="ri-tools-line text-secondary-500 text-xs"></i>
                Ações Futuras
              </h3>
              <div className="space-y-1.5">
                {[
                  { label: "Upload GeoJSON do perímetro", ready: false },
                  { label: "Upload de quadras", ready: false },
                  { label: "Upload de árvores cadastradas", ready: false },
                  { label: "Integração com drone", ready: false },
                  { label: "Visualização de ortofoto", ready: false },
                  { label: "Nuvem de pontos", ready: false },
                  { label: "Comparação temporal", ready: false },
                  { label: "Ordens de serviço de poda", ready: false },
                  { label: "Registro de vistoria", ready: false },
                  { label: "Relatório técnico em PDF", ready: false },
                ].map((acao) => (
                  <div key={acao.label} className="flex items-center justify-between">
                    <span className="text-[10px] text-foreground-600">{acao.label}</span>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium ${
                      acao.ready ? "bg-primary-100 text-primary-700" : "bg-background-100 text-foreground-400"
                    }`}>
                      {acao.ready ? "Pronto" : "Previsto"}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </>
        )}

        {activeTab === "camadas" && (
          <div className="space-y-2">
            <p className="text-[10px] text-foreground-500 mb-2">
              Ative ou desative as camadas de visualização no mapa:
            </p>
            {camadas.map((camada) => (
              <label
                key={camada.id}
                className="flex items-center gap-2.5 p-2 rounded-lg hover:bg-background-50 transition-colors cursor-pointer"
              >
                <input
                  type="checkbox"
                  checked={activeLayers.has(camada.id)}
                  onChange={() => onToggleLayer(camada.id)}
                  className="w-3.5 h-3.5 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                />
                <div className="w-3 h-3 rounded-sm shrink-0" style={{ backgroundColor: camada.cor }}></div>
                <span className="text-[11px] text-foreground-700">{camada.label}</span>
              </label>
            ))}
          </div>
        )}

        {activeTab === "infra" && (
          <div className="space-y-3">
            <div className="bg-accent-50 rounded-card border border-accent-200/50 p-3">
              <h3 className="text-xs font-semibold text-accent-800 font-heading mb-1.5">
                Infraestrutura Verde do Entorno
              </h3>
              <p className="text-[10px] text-accent-700 leading-relaxed">
                O Setor Bela Vista está inserido em uma região com forte presença de áreas verdes urbanas.
                O estudo deverá avaliar se a arborização interna do bairro contribui para conectar os fragmentos
                vegetados do entorno.
              </p>
            </div>

            <div className="space-y-2">
              {areasVerdes.map((av) => (
                <div
                  key={av.nome}
                  className="bg-white rounded-card border border-background-200/70 p-2.5 hover:border-accent-300/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-[11px] font-semibold text-foreground-800 truncate">{av.nome}</p>
                      <p className="text-[9px] text-foreground-500">{av.tipo}</p>
                    </div>
                    <span className={`text-[9px] px-1.5 py-0.5 rounded-full font-medium shrink-0 whitespace-nowrap ${
                      av.relevancia.includes("Alta")
                        ? "bg-primary-100 text-primary-700"
                        : "bg-caution-100 text-caution-700"
                    }`}>
                      {av.relevancia}
                    </span>
                  </div>
                  <div className="flex items-center gap-3 mt-1.5">
                    <span className="text-[9px] text-foreground-500 flex items-center gap-0.5">
                      <i className="ri-compass-line text-[9px]"></i>
                      {av.direcao}
                    </span>
                    <span className="text-[9px] text-foreground-500 flex items-center gap-0.5">
                      <i className="ri-pin-distance-line text-[9px]"></i>
                      {av.distanciaKm} km
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}